// Nivelurile automate de suport/rezistență, desenate pe canvas.
//
// Nu folosim `createPriceLine` din Lightweight Charts pentru că acela desenează pe toată
// lățimea și nu poate purta o etichetă în interiorul graficului. ScalpX trage linia din
// bara care a format nivelul spre dreapta și pune eticheta `preț ×N` deasupra ei —
// exact ce face stratul acesta.

import { el, priceDigits, fmtPrice } from '../core/util.js';
import { withAlpha } from '../core/theme.js';

/**
 * Culorile lor, luate din `levelStyle` și din primitiva de randare.
 *
 * Obiectul se modifică la schimbarea temei, nu se înlocuiește: straturile deja
 * create citesc din el la fiecare randare, deci prind noua paletă fără recreare.
 */
export const LEVEL_STYLE = {
  line: '#969aa8',
  lineBroken: 'rgba(150, 154, 168, 0.45)',
  label: 'rgba(178, 182, 198, 0.9)',
  labelBroken: 'rgba(178, 182, 198, 0.5)',
  width: 1,
};

/** Aliniază nivelurile la tema activă; variantele „rupte" sunt aceleași, mai palide. */
export function applyLevelTheme(theme) {
  LEVEL_STYLE.line = theme.levelLine;
  LEVEL_STYLE.lineBroken = withAlpha(theme.levelLine, 0.45);
  LEVEL_STYLE.label = theme.levelLabel;
  LEVEL_STYLE.labelBroken = withAlpha(theme.levelLabel, 0.5);
}

export class LevelLayer {
  /**
   * @param {Chart} chart  instanța noastră de grafic
   * @param {HTMLElement} host  containerul panoului principal
   */
  constructor(chart, host) {
    this.chart = chart;
    this.host = host;
    this.levels = [];
    this.visible = true;

    this.canvas = el('canvas', { class: 'chart-levels-layer' });
    this.ctx = this.canvas.getContext('2d');
    // Sub stratul de desen, ca desenele utilizatorului să rămână deasupra.
    this.host.prepend(this.canvas);

    this.resizeObserver = new ResizeObserver(() => this.#resize());
    this.resizeObserver.observe(this.host);
    this.#resize();
    // Redesenarea la mișcarea graficului o comandă `Chart`, care urmărește și scara
    // de preț, nu doar pe cea de timp.
  }

  #resize() {
    const dpr = window.devicePixelRatio || 1;
    const w = this.host.clientWidth;
    const h = this.host.clientHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.render();
  }

  setLevels(levels) {
    this.levels = levels || [];
    this.render();
  }

  setVisible(visible) {
    this.visible = visible;
    this.render();
  }

  render() {
    const ctx = this.ctx;
    if (!ctx) return;
    const w = this.canvas.width / (window.devicePixelRatio || 1);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    if (!this.visible || !this.levels.length) return;

    const series = this.chart.candles;
    const compact = this.chart.compact;

    ctx.save();
    ctx.lineWidth = LEVEL_STYLE.width;
    ctx.font = `${compact ? 9 : 10}px Manrope, sans-serif`;
    ctx.textBaseline = 'alphabetic';

    // Nivelurile apropiate ar suprapune etichetele într-o pată ilizibilă:
    // linia se desenează întotdeauna, eticheta doar dacă are loc.
    const labelGap = compact ? 11 : 13;
    const usedRows = [];

    for (const level of this.levels) {
      const y = series.priceToCoordinate(level.price);
      if (y === null) continue;

      // Linia pornește din bara care a format nivelul; dacă aceea a ieșit din
      // vedere spre stânga, pornim de la marginea graficului.
      const anchor = this.chart.toPixel({ time: level.time, price: level.price });
      const x0 = Math.max(0, anchor ? anchor.x : 0);

      ctx.strokeStyle = level.broken ? LEVEL_STYLE.lineBroken : LEVEL_STYLE.line;
      ctx.beginPath();
      ctx.moveTo(x0, y);
      ctx.lineTo(w, y);
      ctx.stroke();

      if (usedRows.some((row) => Math.abs(row - y) < labelGap)) continue;
      usedRows.push(y);

      const digits = priceDigits(level.price);
      const text = fmtPrice(level.price, digits) + (level.touches > 1 ? ` ×${level.touches}` : '');
      ctx.fillStyle = level.broken ? LEVEL_STYLE.labelBroken : LEVEL_STYLE.label;
      ctx.fillText(text, x0 + 4, y - 4);
    }
    ctx.restore();
  }

  destroy() {
    this.resizeObserver?.disconnect();
    this.canvas.remove();
  }
}
